import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { Edit, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Usuario } from "./interface/Usuario.interface";

interface UsuariosListProps {
  onEdit: (usuario: Usuario) => void;
  onDelete: (id: number) => void;
  refreshTrigger?: number;
}

const UsuariosList = ({ onEdit, onDelete, refreshTrigger }: UsuariosListProps) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/usuarios");
      setUsuarios(response.result || []);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os usuários",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [refreshTrigger]);

  const filteredUsuarios = usuarios.filter((usuario) =>
    usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (usuario.username && usuario.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getPerfilLabel = (perfilId: number) => {
    switch (perfilId) {
      case 1:
        return "Administrador";
      case 2:
        return "Médico";
      case 3:
        return "Operador";
      default:
        return "Desconhecido";
    }
  };

  const getTipoUsuarioLabel = (tipoId: number) => {
    switch (tipoId) {
      case 1:
        return "Administrador";
      case 2:
        return "Médico";
      case 3:
        return "Operador";
      default:
        return "Desconhecido";
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Carregando usuários...</div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por e-mail ou username..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Perfil</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Nenhum usuário encontrado
                </TableCell>
              </TableRow>
            ) : (
              filteredUsuarios.map((usuario) => (
                <TableRow key={usuario.id}>
                  <TableCell>{usuario.pessoa.nome}</TableCell>
                  <TableCell className="font-medium">{usuario.email}</TableCell>
                  <TableCell>{usuario.username || "-"}</TableCell>
                  <TableCell>{usuario.pessoa.pessoaFisica?.cpf || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {usuario.perfil.nome}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {usuario.tipoUsuario.descricao}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={usuario.ativo ? "default" : "secondary"}>
                      {usuario.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(usuario)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(usuario.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default UsuariosList;
